import useTabStore from "@/stores/useTabStore";

/** The hardcoded starter tab, seeded client-side in tabsConfig.ts. */
const DEFAULT_TAB_ID = "default";

/**
 * Tidies up the tab bar once onboarding has built its dashboards.
 *
 * Two problems it solves, both from `importTab` appending and claiming focus:
 *
 * 1. **The generic Home tab is still first.** It is seeded client-side on auth
 *    (`getDefaultTabs`) and never saved, which is why onboarding fires at all —
 *    the gate checks the *server* for tabs. Leaving it means someone answers
 *    three screens and lands on the pre-answer answer, duplicating the
 *    headlines and calendar their tailored dashboard already leads with.
 *
 * 2. **The bonus tab steals focus.** `importTab` makes whatever it created
 *    active, so building a second dashboard left the user looking at the
 *    extra one rather than the one they asked for.
 *
 * Only ever called on completion. Anyone who skips or dismisses keeps Home
 * untouched — for them it is still the only answer we have.
 */
export function finishOnboardingTabs(primaryTabId: string | null): void {
  const store = useTabStore.getState();

  // Never leave the user with nothing. If the imports somehow produced no
  // tabs, Home is better than an empty dashboard.
  const others = store.tabs.filter((tab) => tab.id !== DEFAULT_TAB_ID);
  if (others.length === 0) return;

  if (store.tabs.some((tab) => tab.id === DEFAULT_TAB_ID)) {
    store.removeTab(DEFAULT_TAB_ID);
  }

  // Set focus after the removal: removeTab falls back to "default" by name
  // when the tab it removed was active, which would point at a tab that no
  // longer exists.
  const target =
    primaryTabId && others.some((tab) => tab.id === primaryTabId)
      ? primaryTabId
      : others[0].id;
  useTabStore.setState({ activeTabId: target });
}

/**
 * The tab `applyLayoutData` just created.
 *
 * `importTab` doesn't return an id, but it does make the new tab active, so
 * reading that immediately afterwards identifies it. Cheaper than threading a
 * return value through the shared apply path for one caller's benefit.
 */
export function lastImportedTabId(): string | null {
  return useTabStore.getState().activeTabId ?? null;
}
